/**
 * useCohortData Hook
 * 
 * Custom hook for fetching cohort analysis data.
 * Wraps useAdminData with cohort-specific configuration.
 * 
 * Features:
 * - 24-hour client-side caching (computationally intensive)
 * - Support for groupBy (week/month) and tier filters
 * - Loading, error, and success states
 * - Automatic data formatting
 * 
 * Requirements: 4.25
 */

import { useAdminData } from '../components/shared/useAdminData';
import { API_ENDPOINTS, buildApiUrl } from '../config/api-endpoints';

/**
 * Fetch cohort analysis data
 * @param {string} groupBy - 'week' or 'month'
 * @param {string} tier - 'all', 'free', 'premium', or 'family'
 * @returns {Object} { data, loading, error, refresh }
 */
export const useCohortData = (groupBy = 'month', tier = 'all') => {
  // Validate parameters
  const validGroupBy = ['week', 'month'];
  const validTiers = ['all', 'free', 'premium', 'family'];
  
  const sanitizedGroupBy = validGroupBy.includes(groupBy) ? groupBy : 'month';
  const sanitizedTier = validTiers.includes(tier) ? tier : 'all';

  // Use useAdminData hook with cohort-specific configuration
  const result = useAdminData(
    `cohort-data-${sanitizedGroupBy}-${sanitizedTier}`,
    buildApiUrl(API_ENDPOINTS.ADMIN.ANALYTICS_HANDLER, `/analytics/cohorts`),
    {
      params: {
        groupBy: sanitizedGroupBy,
        tier: sanitizedTier
      },
      ttl: 86400000, // 24 hours (24 * 60 * 60 * 1000)
      enabled: true
    }
  );

  return {
    ...result,
    // Add formatted data helpers
    cohorts: result.data?.cohorts || [],
    averageRetention: result.data?.averageRetention || null,
    bestCohort: result.data?.bestCohort || null,
    worstCohort: result.data?.worstCohort || null,
    churnWarnings: result.data?.churnWarnings || []
  };
};

export default useCohortData;
